import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

const ImageMapWithModal = ({ 
  imageSrc, 
  imageAlt, 
  areas = [], 
  width = '100%', 
  height = 'auto',
  style = {},
  usePercentageCoords = false
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [naturalDimensions, setNaturalDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);
  const theme = useTheme();
  // const [selectMode, setSelectMode] = useState(false);
  // const [selection, setSelection] = useState(null); // {x1, y1, x2, y2}
  // const [startPoint, setStartPoint] = useState(null);

  useEffect(() => {
    const img = imageRef.current;
    if (img) {
      const handleLoad = () => {
        setImageDimensions({
          width: img.offsetWidth,
          height: img.offsetHeight
        });
        setNaturalDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };

      if (img.complete) {
        handleLoad();
      } else {
        img.addEventListener('load', handleLoad);
        return () => img.removeEventListener('load', handleLoad);
      }
    }
  }, [imageSrc]);

  const handleAreaClick = (event, area) => {
    event.preventDefault();
    setSelectedArea(area);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedArea(null);
  };

  const getScaledCoordinates = (coords, currentWidth, currentHeight) => {
    const coordArray = coords.split(',').map(coord => parseInt(coord.trim()));
    
    if (coordArray.length === 4) {
      // Rectangle: x1, y1, x2, y2
      const [x1, y1, x2, y2] = coordArray;
      const scaleX = currentWidth / naturalDimensions.width;
      const scaleY = currentHeight / naturalDimensions.height;
      
      return [
        x1 * scaleX,
        y1 * scaleY,
        x2 * scaleX,
        y2 * scaleY
      ];
    } else if (coordArray.length === 3) {
      // Circle: x, y, radius
      const [x, y, radius] = coordArray;
      const scaleX = currentWidth / naturalDimensions.width;
      const scaleY = currentHeight / naturalDimensions.height;
      
      return [
        x * scaleX,
        y * scaleY,
        radius * Math.min(scaleX, scaleY)
      ];
    }
    
    return coordArray;
  };

  const getPercentageCoordinates = (coords, originalWidth, originalHeight) => {
    const coordArray = coords.split(',').map(coord => parseFloat(coord.trim()));
    
    if (coordArray.length === 4) {
      // Rectangle: x1, y1, x2, y2
      const [x1, y1, x2, y2] = coordArray;
      
      return [
        (x1 / originalWidth) * 100,
        (y1 / originalHeight) * 100,
        (x2 / originalWidth) * 100,
        (y2 / originalHeight) * 100
      ];
    } else if (coordArray.length === 3) {
      // Circle: x, y, radius
      const [x, y, radius] = coordArray;
      
      return [
        (x / originalWidth) * 100,
        (y / originalHeight) * 100,
        (radius / Math.min(originalWidth, originalHeight)) * 100
      ];
    }
    
    return coordArray;
  };

  // Selection mode handlers
  // const handleMouseDown = (e) => {
  //   if (!selectMode) return;
  //   const img = imageRef.current;
  //   const rect = img.getBoundingClientRect();
  //   const scaleX = img.naturalWidth / img.offsetWidth;
  //   const scaleY = img.naturalHeight / img.offsetHeight;
  //   setStartPoint({
  //     x: (e.clientX - rect.left) * scaleX,
  //     y: (e.clientY - rect.top) * scaleY
  //   });
  //   setSelection(null);
  // };

  // const handleMouseMove = (e) => {
  //   if (!selectMode || !startPoint) return;
  //   const img = imageRef.current;
  //   const rect = img.getBoundingClientRect();
  //   const scaleX = img.naturalWidth / img.offsetWidth;
  //   const scaleY = img.naturalHeight / img.offsetHeight;
  //   setSelection({
  //     x1: startPoint.x,
  //     y1: startPoint.y,
  //     x2: (e.clientX - rect.left) * scaleX,
  //     y2: (e.clientY - rect.top) * scaleY
  //   });
  // };

  // const handleMouseUp = (e) => {
  //   if (!selectMode || !startPoint) return;
  //   const img = imageRef.current;
  //   const rect = img.getBoundingClientRect();
  //   const scaleX = img.naturalWidth / img.offsetWidth;
  //   const scaleY = img.naturalHeight / img.offsetHeight;
  //   const x2 = (e.clientX - rect.left) * scaleX;
  //   const y2 = (e.clientY - rect.top) * scaleY;
  //   setSelection({
  //     x1: startPoint.x,
  //     y1: startPoint.y,
  //     x2,
  //     y2
  //   });
  //   setStartPoint(null);
  // };

  return (
    <Box sx={{ position: 'relative', width: '100%', margin: '0 auto' }}>
      {/*
      <Button
        variant={selectMode ? 'contained' : 'outlined'}
        color={selectMode ? 'secondary' : 'primary'}
        size="small"
        sx={{ position: 'absolute', zIndex: 30, top: 8, left: 8 }}
        onClick={() => {
          setSelectMode((m) => !m);
          setSelection(null);
          setStartPoint(null);
        }}
      >
        {selectMode ? 'Exit Selection Mode' : 'Select Area'}
      </Button>
      */}
      <img
        ref={imageRef}
        src={imageSrc}
        alt={imageAlt}
        style={{ 
          width: width, 
          height: height,
          ...style,
          // cursor: selectMode ? 'crosshair' : 'pointer'
        }}
        // onMouseDown={selectMode ? handleMouseDown : undefined}
        // onMouseMove={selectMode ? handleMouseMove : undefined}
        // onMouseUp={selectMode ? handleMouseUp : undefined}
        draggable={false}
      />
      {/*
      {selectMode && selection && (
        <Box
          sx={{
            position: 'absolute',
            left: `${Math.min(selection.x1, selection.x2)}px`,
            top: `${Math.min(selection.y1, selection.y2)}px`,
            width: `${Math.abs(selection.x2 - selection.x1)}px`,
            height: `${Math.abs(selection.y2 - selection.y1)}px`,
            border: `2px dashed ${theme.palette.secondary.main}`,
            background: 'rgba(255,0,0,0.07)',
            pointerEvents: 'none',
            zIndex: 25
          }}
        />
      )}
      {selectMode && selection && (
        <Box
          sx={{
            position: 'absolute',
            top: `${Math.min(selection.y1, selection.y2) - 28}px`,
            left: `${Math.min(selection.x1, selection.x2)}px`,
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: 13,
            zIndex: 40
          }}
        >
          {`Coords: ${Math.round(Math.min(selection.x1, selection.x2))}, ${Math.round(Math.min(selection.y1, selection.y2))}, ${Math.round(Math.max(selection.x1, selection.x2))}, ${Math.round(Math.max(selection.y1, selection.y2))}`}
        </Box>
      )}
      */}
      
      {/* Overlay areas for click detection */}
      {areas.map((area, index) => {
        let coords;
        let areaStyle = {};
        
        if (usePercentageCoords && area.percentageCoords) {
          // Use percentage-based coordinates for consistent highlighting
          coords = area.percentageCoords.split(',').map(coord => parseFloat(coord.trim()));
        } else {
          // Use scaled pixel coordinates based on natural image dimensions
          coords = getScaledCoordinates(
            area.coords, 
            imageDimensions.width,
            imageDimensions.height
          );
        }
        
        // Highlight color support
        const highlight = area.highlightColor;
        const baseBg = highlight ? highlight : 'rgba(255, 255, 255, 0.03)';
        const hoverBg = highlight ? highlight.replace(/(0?\.?[0-9]+)\)$/,'0.18)') : 'rgba(255, 255, 255, 0.08)';
        const borderColor = highlight ? highlight.replace(/(0?\.?[0-9]+)\)$/,'0.5)') : 'rgba(255, 255, 255, 0.15)';
        
        if (area.shape === 'rect') {
          if (usePercentageCoords && area.percentageCoords) {
            const [x1, y1, x2, y2] = coords;
            areaStyle = {
              position: 'absolute',
              left: `${x1}%`,
              top: `${y1}%`,
              width: `${x2 - x1}%`,
              height: `${y2 - y1}%`,
              cursor: 'pointer',
              backgroundColor: baseBg,
              border: `1.5px solid ${borderColor}`,
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: hoverBg,
              }
            };
          } else {
            const [x1, y1, x2, y2] = coords;
            areaStyle = {
              position: 'absolute',
              left: `${x1}px`,
              top: `${y1}px`,
              width: `${x2 - x1}px`,
              height: `${y2 - y1}px`,
              cursor: 'pointer',
              backgroundColor: baseBg,
              border: `1.5px solid ${borderColor}`,
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: hoverBg,
              }
            };
          }
        } else if (area.shape === 'circle') {
          if (usePercentageCoords && area.percentageCoords) {
            const [x, y, radius] = coords;
            areaStyle = {
              position: 'absolute',
              left: `${x - radius}%`,
              top: `${y - radius}%`,
              width: `${radius * 2}%`,
              height: `${radius * 2}%`,
              borderRadius: '50%',
              cursor: 'pointer',
              backgroundColor: baseBg,
              border: `1.5px solid ${borderColor}`,
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: hoverBg,
              }
            };
          } else {
            const [x, y, radius] = coords;
            areaStyle = {
              position: 'absolute',
              left: `${x - radius}px`,
              top: `${y - radius}px`,
              width: `${radius * 2}px`,
              height: `${radius * 2}px`,
              borderRadius: '50%',
              cursor: 'pointer',
              backgroundColor: baseBg,
              border: `1.5px solid ${borderColor}`,
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: hoverBg,
              }
            };
          }
        }

        return (
          <Box
            key={index}
            sx={areaStyle}
            onClick={(event) => handleAreaClick(event, area)}
            title={area.alt}
          />
        );
      })}

      {/* Modal for displaying area content */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedArea?.alt}
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              width: '5%',
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedArea?.component ? (
            selectedArea.component
          ) : selectedArea?.content ? (
            <div dangerouslySetInnerHTML={{ __html: selectedArea.content }} />
          ) : (
            <Typography>
              {selectedArea?.description || `Information about ${selectedArea?.alt}`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
          {selectedArea?.href && (
            <Button 
              onClick={() => {
                window.open(selectedArea.href, '_blank');
                handleCloseModal();
              }}
              variant="contained"
            >
              Learn More
            </Button>
          )}
          {selectedArea?.unitId && (
            <Button
              onClick={() => {
                window.open(`/#/unit-payment/${selectedArea.unitId}`, '_blank');
                handleCloseModal();
              }}
              variant="contained"
              color="primary"
            >
              Go to Payment
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageMapWithModal; 